import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as api from '../../../../../api/generated';
import ListItemCreateButton from '../../../../../components/custom/ListItemCreateButton';
import { useFetchedValue } from '../../../../../components/hooks/useFetched';
import { usePageableGrid } from '../../../../../components/hooks/usePageableGrid';
import { Grid, GridColumn } from '../../../../../components/KendoReact/Grid';
import KendoLoading from '../../../../../components/spinners/KendoLoading';
import { useWindow } from '../../../../../components/windows/WindowContext';
import { IInfoScreenListItem } from '../../../../../models';
import AdministrationActionCell from '../../components/AdministrationActionCell';
import ConnectionStatusCell from '../components/ConnectionStatusCell';
import DeviceLinkCell from '../components/DeviceLinkCell';
import DisplayTypeCell from '../components/DisplayTypeCell';
import InfoScreenCreateModalWindow from './InfoScreenCreateModalWindow';
import InfoScreenEditModalWindow from './InfoScreenEditModalWindow';

const getInfoScreens = async (organizationInformationId: number, skip: number, take: number) =>
    api.administration.getInfoScreens({
        organizationInformationId,
        skip,
        take
    });

const DeviceInfoScreens: FC = () => {
    const { organizationInformationId } = useParams<'organizationInformationId'>();
    const { openWindow } = useWindow();
    const [isLoading, setIsLoading] = useState(false);
    const [gridPageProps] = usePageableGrid([5, 10, 15, 20, 25], 15);
    const [infoScreens, refetchInfoScreens, , , isInfoScreensLoading] = useFetchedValue(
        getInfoScreens,
        true,
        parseInt(organizationInformationId.toString()),
        gridPageProps.skip,
        gridPageProps.take
    );

    const openInfoScreenEditModalWindow = async (infoScreenId: number) => {
        try {
            InfoScreenEditModalWindow.defaultProps = {
                componentProps: {
                    infoScreenId: infoScreenId,
                    refetchInfoScreens: refetchInfoScreens
                }
            };
            openWindow({
                title: 'Info ekrano redagavimas',
                component: InfoScreenEditModalWindow,
                isDialogBehaviour: true,
                width: 800,
                height: 400
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    const openInfoScreenCreateModalWindow = async (basedOnInitialValues: IInfoScreenListItem | null) => {
        try {
            InfoScreenCreateModalWindow.defaultProps = {
                componentProps: {
                    refetchInfoScreens: refetchInfoScreens,
                    basedOnInitialValues: basedOnInitialValues,
                    openInfoScreenCreateModalWindow: openInfoScreenCreateModalWindow
                }
            };
            openWindow({
                title: 'Info ekrano kūrimas',
                component: InfoScreenCreateModalWindow,
                isDialogBehaviour: true,
                width: 800,
                height: 400
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    const deleteInfoScreen = async (infoScreenId: number) => {
        try {
            setIsLoading(true);
            await api.administration.deleteInfoScreen(infoScreenId);
        } catch (ex) {
            toast.error('Nepavyko ištrinti info ekrano');
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) return <KendoLoading />;

    return (
        <div>
            <ListItemCreateButton openCreateModal={() => openInfoScreenCreateModalWindow(null)} />
            <Grid className="mt-2" {...gridPageProps} data={infoScreens} isLoading={isInfoScreensLoading}>
                <GridColumn
                    width="200px"
                    title="Veiksmai"
                    cell={(c) => (
                        <AdministrationActionCell
                            {...c}
                            fetchData={refetchInfoScreens}
                            deleteItem={deleteInfoScreen}
                            editItem={openInfoScreenEditModalWindow}
                        />
                    )}
                />
                <GridColumn minResizableWidth={100} width={300} title="Pavadinimas" field="name" />
                <GridColumn minResizableWidth={100} width={150} title="Modelis" field="model" />

                <GridColumn minResizableWidth={50} width={100} title="Statusas" cell={(c) => <ConnectionStatusCell {...c} />} />
                <GridColumn
                    minResizableWidth={130}
                    width={130}
                    title="Atvaizdavimo tipas"
                    cell={(c) => <DisplayTypeCell {...c} device={'infoScreen'} />}
                />
                <GridColumn minResizableWidth={100} width={150} title="Fizinė lokacija" field="locationName" />
                <GridColumn
                    minResizableWidth={100}
                    title="Įrenginio rodomas vaizdas"
                    cell={(c) => <DeviceLinkCell deviceId={parseInt(c.dataItem.id)} device={'infoScreen'} />}
                />
            </Grid>
        </div>
    );
};

export default DeviceInfoScreens;
